// F-U / F-W: batch Publish / Unpublish / Generate QR (+ F-V Generate
// Narration Audio) for the selected exhibits or zones.
//
// - Each button counts the selected rows it applies to; the rest are skipped
//   (not errors). Generate QR only fills MISSING codes, never regenerates.
// - Publish / Unpublish confirm first (visitors see the change at once);
//   Generate QR runs straight away.
// - Each item succeeds or fails on its own (useContentBatch). Failures are
//   listed with the server's message and an Edit link; afterwards only the
//   failed rows stay selected.

import { useState, type FC } from "react";
import { toast } from "sonner";
import { Button } from "@shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/components/ui/dialog";
import {
  useContentBatch,
  type ContentBatchAction,
  type ContentBatchResult,
  type ContentKind,
} from "@shared/api/hooks/useContentBatch";
import GenerateNarrationButton from "@staff/features/narration/GenerateNarrationButton";

export const BATCH_LIMIT = 200;

/** The row fields the batch bar reads (exhibit and zone list rows). */
export interface BatchRow {
  id: string;
  code?: string | null;
  titleTc?: string | null;
  state: string;
  hasQr?: boolean | null;
  narrationStatus?: Record<string, string> | null;
}

const NOUN: Record<ContentKind, [string, string]> = {
  exhibit: ["exhibit", "exhibits"],
  zone: ["zone", "zones"],
};

const ACTION_META: Record<
  ContentBatchAction,
  { button: string; verb: string; done: string; skipped: string }
> = {
  publish: {
    button: "Publish",
    verb: "Publishing",
    done: "published",
    skipped: "already published",
  },
  unpublish: {
    button: "Unpublish",
    verb: "Unpublishing",
    done: "unpublished",
    skipped: "already unpublished",
  },
  "generate-qr": {
    button: "Generate QR",
    verb: "Generating QR codes",
    done: "QR codes generated",
    skipped: "already have a QR code",
  },
};

/** Which selected rows an action applies to. */
export function applicableRows(
  action: ContentBatchAction,
  rows: BatchRow[],
): BatchRow[] {
  switch (action) {
    case "publish":
      return rows.filter((r) => r.state === "draft");
    case "unpublish":
      return rows.filter((r) => r.state === "published");
    case "generate-qr":
      return rows.filter((r) => !r.hasQr);
  }
}

const disabledReason = (action: ContentBatchAction, nouns: string): string =>
  ({
    publish: `All selected ${nouns} are already published`,
    unpublish: `All selected ${nouns} are already unpublished`,
    "generate-qr": `All selected ${nouns} already have a QR code`,
  })[action];

export interface ContentBatchBarProps {
  kind: ContentKind;
  selectedRows: BatchRow[];
  onClearSelection: () => void;
  /** Called with the ids that failed — they stay selected. */
  onFinished: (failedIds: string[]) => void;
  onEdit: (id: string) => void;
}

interface Outcome {
  action: ContentBatchAction;
  result: ContentBatchResult;
  rowsById: Map<string, BatchRow>;
}

const ContentBatchBar: FC<ContentBatchBarProps> = ({
  kind,
  selectedRows,
  onClearSelection,
  onFinished,
  onEdit,
}) => {
  const runBatch = useContentBatch();
  const [noun, nouns] = NOUN[kind];
  const [confirming, setConfirming] = useState<ContentBatchAction | null>(null);
  const [progress, setProgress] = useState<{
    action: ContentBatchAction;
    done: number;
    total: number;
  } | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const overLimit = selectedRows.length > BATCH_LIMIT;
  const running = progress !== null;

  const run = async (action: ContentBatchAction) => {
    setConfirming(null);
    const targets = applicableRows(action, selectedRows);
    if (!targets.length) return;
    setProgress({ action, done: 0, total: targets.length });
    const result = await runBatch(
      kind,
      action,
      targets.map((r) => r.id),
      (done, total) => setProgress({ action, done, total }),
    );
    setProgress(null);
    const meta = ACTION_META[action];
    if (!result.failed.length) {
      toast.success(`${result.succeeded.length} ${meta.done}`);
      onFinished([]);
      return;
    }
    setOutcome({
      action,
      result,
      rowsById: new Map(targets.map((r) => [r.id, r])),
    });
  };

  const closeOutcome = () => {
    if (outcome) onFinished(outcome.result.failed.map((f) => f.id));
    setOutcome(null);
  };

  const actionButton = (action: ContentBatchAction) => {
    const count = applicableRows(action, selectedRows).length;
    const disabled = running || overLimit || count === 0;
    return (
      <Button
        type="button"
        size="sm"
        variant={action === "publish" ? "default" : "outline"}
        disabled={disabled}
        title={
          overLimit
            ? `Select ${BATCH_LIMIT} or fewer`
            : count === 0
              ? disabledReason(action, nouns)
              : undefined
        }
        onClick={() =>
          action === "generate-qr" ? void run(action) : setConfirming(action)
        }
        data-testid={`batch-${action}`}
      >
        {ACTION_META[action].button} ({count})
      </Button>
    );
  };

  const confirmTargets = confirming
    ? applicableRows(confirming, selectedRows)
    : [];

  return (
    <>
      <div
        className="sticky top-0 z-20 mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-white px-4 py-2 shadow-sm"
        data-testid="batch-bar"
      >
        <span className="text-sm font-medium" data-testid="batch-count">
          {selectedRows.length} selected
        </span>
        {actionButton("publish")}
        {actionButton("unpublish")}
        {actionButton("generate-qr")}
        {/* F-V / F-W: rows with at least one missing narration slot */}
        <GenerateNarrationButton
          kind={kind}
          ids={selectedRows.map((r) => r.id)}
          missingCount={
            selectedRows.filter((r) =>
              Object.values(r.narrationStatus ?? {}).includes("missing"),
            ).length
          }
          testId="batch-generate-narration"
        />
        {overLimit && (
          <span className="text-sm text-red-600" data-testid="batch-limit">
            Select {BATCH_LIMIT} or fewer
          </span>
        )}
        {progress && (
          <span
            className="text-sm text-muted-foreground"
            data-testid="batch-progress"
          >
            {ACTION_META[progress.action].verb}… {progress.done} of{" "}
            {progress.total}
          </span>
        )}
        <button
          type="button"
          className="ml-auto text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
          onClick={onClearSelection}
          disabled={running}
          data-testid="batch-clear"
        >
          Clear ✕
        </button>
      </div>

      {/* Publish / Unpublish confirmation */}
      <Dialog
        open={confirming !== null}
        onOpenChange={(open) => !open && setConfirming(null)}
      >
        <DialogContent data-testid="batch-confirm-dialog">
          {confirming && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {ACTION_META[confirming].button} {nouns}
                </DialogTitle>
                <DialogDescription>
                  {confirmTargets.length}{" "}
                  {confirmTargets.length === 1 ? noun : nouns} will be{" "}
                  {ACTION_META[confirming].done}.
                  {selectedRows.length > confirmTargets.length &&
                    ` ${selectedRows.length - confirmTargets.length} ${
                      ACTION_META[confirming].skipped
                    } will be skipped.`}
                </DialogDescription>
              </DialogHeader>
              <p className="text-sm">
                {confirming === "publish"
                  ? `Published ${nouns} become visible to visitors immediately.`
                  : `Unpublished ${nouns} disappear from the visitor app immediately.`}
              </p>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirming(null)}
                  data-testid="batch-confirm-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => void run(confirming)}
                  data-testid="batch-confirm-run"
                >
                  {ACTION_META[confirming].button} {confirmTargets.length}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Results with failures */}
      <Dialog
        open={outcome !== null}
        onOpenChange={(open) => !open && closeOutcome()}
      >
        <DialogContent data-testid="batch-result-dialog">
          {outcome && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {ACTION_META[outcome.action].button} finished
                </DialogTitle>
                <DialogDescription>
                  ✓ {outcome.result.succeeded.length}{" "}
                  {ACTION_META[outcome.action].done} · ✕{" "}
                  {outcome.result.failed.length} failed
                </DialogDescription>
              </DialogHeader>
              <ul className="max-h-72 overflow-y-auto divide-y text-sm">
                {outcome.result.failed.map((f) => {
                  const row = outcome.rowsById.get(f.id);
                  return (
                    <li
                      key={f.id}
                      className="flex items-start gap-3 py-2"
                      data-testid={`batch-failure-${f.id}`}
                    >
                      <span className="font-mono">{row?.code || "---"}</span>
                      <span className="flex-1">
                        <span className="block">{row?.titleTc || "—"}</span>
                        <span className="block text-red-600">{f.message}</span>
                      </span>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={() => {
                          closeOutcome();
                          onEdit(f.id);
                        }}
                        data-testid={`batch-failure-edit-${f.id}`}
                      >
                        Edit
                      </Button>
                    </li>
                  );
                })}
              </ul>
              <DialogFooter>
                <Button
                  type="button"
                  onClick={closeOutcome}
                  data-testid="batch-result-close"
                >
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContentBatchBar;
